import { useCallback, useMemo, useState } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import { useScrollToError } from '~/components/shared/forms';
import { computeInterviewDurationMinutes, type TOnsiteInterviewFormData } from './FormRule';

const formatHoursMinutes = (minutes: number): string => {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

// "4-hour" when whole hours, otherwise "4 hr 30 min" — used as the {limit}
// substitution inside the exceeds-limit banner copy.
const formatLimitLabel = (timeLimitInMinutes: number): string => {
  const safe = Math.max(0, Math.round(timeLimitInMinutes));
  if (safe > 0 && safe % 60 === 0) return `${safe / 60}-hour`;
  return formatHoursMinutes(safe);
};

const buildExceedsLimitMessage = (durationMinutes: number, timeLimitInMinutes: number): string =>
  `Arrival/Departure exceeds ${formatLimitLabel(timeLimitInMinutes)} limit ` +
  `(currently ${formatHoursMinutes(durationMinutes)}). Update either time to proceed.`;

export interface IOnsiteInterviewFormSubmitV2Args {
  control: Control<TOnsiteInterviewFormData>;
  timeLimitInMinutes: number;
}

export interface IOnsiteInterviewFormSubmitV2Result {
  hasAttemptedSubmit: boolean;
  markAttemptedSubmit: () => void;
  /** True when *either* banner is visible — drives scroll-into-view. */
  showBanner: boolean;
  /** True when one or more required fields are empty after a submit attempt. */
  showMissingRequiredBanner: boolean;
  /** True when the arrival→departure window exceeds the configured time limit. */
  showExceedsLimitBanner: boolean;
  /** Pre-formatted message for the exceeds-limit banner (includes current h/m). */
  exceedsLimitMessage: string;
  bannerRef: ReturnType<typeof useScrollToError>;
  fieldErrors: {
    dates: boolean;
    specialty: boolean;
    hoursArrival: boolean;
    hoursDeparture: boolean;
    hourlyRate: boolean;
  };
}

/**
 * Owns the "submission attempt" UX for `OnsiteInterviewFormV2`:
 *  - `hasAttemptedSubmit` flips on once the user has clicked Next.
 *  - `showMissingRequiredBanner` and `showExceedsLimitBanner` each render
 *    their own ribbon; when both fire on the same submit they render
 *    stacked (required-fields first, exceeds-limit second).
 *  - `exceedsLimitMessage` is pre-formatted with the current selected
 *    window so the banner can show "current hours and minutes" per AC.
 *  - `fieldErrors` flags drive the red highlight on each input.
 *  - `bannerRef` is wired through `useScrollToError` so whichever banner is
 *    visible first scrolls itself into view when it appears.
 */
export const useOnsiteInterviewFormSubmitV2 = ({
  control,
  timeLimitInMinutes,
}: IOnsiteInterviewFormSubmitV2Args): IOnsiteInterviewFormSubmitV2Result => {
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const dates = useWatch({ control, name: 'dates' });
  const specialty = useWatch({ control, name: 'specialty' });
  const hoursArrival = useWatch({ control, name: 'hoursArrival' });
  const hoursDeparture = useWatch({ control, name: 'hoursDeparture' });
  const hourlyRate = useWatch({ control, name: 'hourlyRate' });

  const isMissingRequired =
    !dates?.length || !specialty || !hoursArrival || !hoursDeparture || !hourlyRate;

  const durationMinutes = useMemo(
    () => computeInterviewDurationMinutes(hoursArrival, hoursDeparture),
    [hoursArrival, hoursDeparture],
  );
  const exceedsLimit = durationMinutes > timeLimitInMinutes;

  const showMissingRequiredBanner = hasAttemptedSubmit && isMissingRequired;
  const showExceedsLimitBanner = hasAttemptedSubmit && exceedsLimit;
  const showBanner = showMissingRequiredBanner || showExceedsLimitBanner;
  const bannerRef = useScrollToError(showBanner);

  const exceedsLimitMessage = useMemo(
    () => buildExceedsLimitMessage(durationMinutes, timeLimitInMinutes),
    [durationMinutes, timeLimitInMinutes],
  );

  const fieldErrors = useMemo(
    () => ({
      dates: hasAttemptedSubmit && !dates?.length,
      specialty: hasAttemptedSubmit && !specialty,
      hoursArrival: hasAttemptedSubmit && (!hoursArrival || exceedsLimit),
      hoursDeparture: hasAttemptedSubmit && (!hoursDeparture || exceedsLimit),
      hourlyRate: hasAttemptedSubmit && !hourlyRate,
    }),
    [
      dates,
      exceedsLimit,
      hasAttemptedSubmit,
      hourlyRate,
      hoursArrival,
      hoursDeparture,
      specialty,
    ],
  );

  const markAttemptedSubmit = useCallback(() => setHasAttemptedSubmit(true), []);

  return {
    hasAttemptedSubmit,
    markAttemptedSubmit,
    showBanner,
    showMissingRequiredBanner,
    showExceedsLimitBanner,
    exceedsLimitMessage,
    bannerRef,
    fieldErrors,
  };
};
